import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [showCallbackModal, setShowCallbackModal] = useState(false);

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    fetch('https://readdy.ai/api/form/d4i0k4musuhg53kqajn0', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then(() => {
        alert('Message sent successfully! We will contact you soon.');
        form.reset();
      })
      .catch(() => {
        alert('Failed to send message. Please try again.');
      });
  };

  const handleCallbackSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    fetch('https://readdy.ai/api/form/d4i0k4musuhg53kqajo0', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(formData as any).toString(),
    })
      .then(() => {
        alert('Callback request submitted! We will call you back soon.');
        setShowCallbackModal(false);
        form.reset();
      })
      .catch(() => {
        alert('Failed to submit request. Please try again.');
      });
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: 'Head Office',
      details: ['Roztylská 1860/1, Praha 11', 'Czech Republic'],
      link: null,
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      details: ['contact@lynora.cz', 'Quick Response'],
      link: 'mailto:contact@lynora.cz',
    },
    {
      icon: 'ri-time-line',
      title: 'Business Hours',
      details: ['Monday - Friday', '9:00 AM - 6:00 PM'],
      link: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative pt-32 pb-24 px-6 lg:px-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
            <i className="ri-customer-service-2-line text-white"></i>
            <span className="text-sm text-white font-semibold">Get in Touch</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Contact <strong>Our Team</strong>
          </h1>

          <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Have questions about our products or services? We're here to help. Reach out to us and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-18 h-18 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg" style={{ width: '4.5rem', height: '4.5rem' }}>
                  <i className={`${info.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-slate-600">
                      {info.link && idx === 0 ? (
                        <a href={info.link} className="hover:text-blue-600 transition-colors duration-300 cursor-pointer">
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
                <i className="ri-mail-send-line text-blue-600"></i>
                <span className="text-sm text-blue-600 font-semibold">Send Message</span>
              </div>

              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Get in <strong>Touch</strong>
              </h2>

              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>

              <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="Your company"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Subject *
                  </label>
                  <select
                    name="subject"
                    required
                    className="w-full px-4 py-3 pr-8 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm appearance-none bg-white cursor-pointer"
                  >
                    <option value="">Select a subject</option>
                    <option value="Product Inquiry">Product Inquiry</option>
                    <option value="Quote Request">Quote Request</option>
                    <option value="Technical Support">Technical Support</option>
                    <option value="Logistics Question">Logistics Question</option>
                    <option value="Partnership">Partnership Opportunity</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none text-sm"
                    placeholder="Tell us about your inquiry... (max 500 characters)"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg font-bold rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Send Message
                </button>
              </form>

              <div className="mt-8 text-center">
                <button
                  onClick={() => setShowCallbackModal(true)}
                  className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Request a Callback
                </button>
              </div>
            </div>

            <div>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
                <i className="ri-mail-line text-blue-600"></i>
                <span className="text-sm text-blue-600 font-semibold">Email Contact</span>
              </div>

              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Direct <strong>Email</strong>
              </h2>

              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                For immediate assistance, send us an email and we'll respond promptly.
              </p>

              <div className="bg-white rounded-xl p-8 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 mb-12">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <i className="ri-mail-line text-white text-4xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">General Inquiries</h3>
                    <a
                      href="mailto:contact@lynora.cz"
                      className="text-lg text-blue-600 hover:text-blue-700 transition-colors duration-300 cursor-pointer"
                    >
                      contact@lynora.cz
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Office Location</h3>
                <p className="text-slate-300 mb-6">
                  Visit our headquarters in Prague, Czech Republic. We're open Monday through Friday, 9:00 AM to 6:00 PM.
                </p>
                <div className="bg-white rounded-xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2562.5!2d14.5!3d50.03!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTDCsDAxJzQ4LjAiTiAxNMKwMzAnMDAuMCJF!5e0!3m2!1sen!2s!4v1234567890"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Office Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showCallbackModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-slate-900">Request Callback</h2>
                <button
                  onClick={() => setShowCallbackModal(false)}
                  className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors duration-300"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <p className="text-slate-600 mb-6">
                Leave your details and we'll call you back at your preferred time.
              </p>

              <form id="callback-form" data-readdy-form onSubmit={handleCallbackSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="+380 00 000 0000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Preferred Time
                  </label>
                  <select
                    name="preferredTime"
                    className="w-full px-4 py-3 pr-8 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm appearance-none bg-white cursor-pointer"
                  >
                    <option value="Morning (9:00-12:00)">Morning (9:00-12:00)</option>
                    <option value="Afternoon (12:00-15:00)">Afternoon (12:00-15:00)</option>
                    <option value="Evening (15:00-18:00)">Evening (15:00-18:00)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Brief Message
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none text-sm"
                    placeholder="What would you like to discuss? (max 500 characters)"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg font-bold rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Request Callback
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ContactPage;
